import { useMemo, useState } from "react";
import { normalizeSearchText } from "../../../shared/utils/searchText";
import type { AuthUser } from "../../auth/shared/types/auth";
import { normalizeCpf } from "../../challenges/utils/challengePairs";

type AdminPlayerSearchInputProps = {
  availableUsers: AuthUser[];
  disabled?: boolean;
  excludeCpf?: string;
  getSelectedValue?: (user: AuthUser) => string;
  id: string;
  onSelect?: (user: AuthUser, value: string) => void;
  onValueChange: (value: string) => void;
  placeholder: string;
  value: string;
};

const MAX_PLAYER_RESULTS = 12;

export function formatAdminPlayerOption(user: AuthUser) {
  return `${user.name} - ${normalizeCpf(user.cpf)}`;
}

export default function AdminPlayerSearchInput({
  availableUsers,
  disabled = false,
  excludeCpf = "",
  getSelectedValue = formatAdminPlayerOption,
  id,
  onSelect,
  onValueChange,
  placeholder,
  value,
}: AdminPlayerSearchInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const normalizedQuery = normalizeSearchText(value);
  const excludedCpf = normalizeCpf(excludeCpf);
  const listboxId = `${id}-results`;

  const filteredUsers = useMemo(() => {
    const matches = availableUsers
      .filter((user) => normalizeCpf(user.cpf) !== excludedCpf)
      .map((user) => {
        const userCpf = normalizeCpf(user.cpf);
        const searchable = normalizeSearchText(
          [
            user.name,
            userCpf,
            user.email,
            user.category,
            formatAdminPlayerOption(user),
          ]
            .filter(Boolean)
            .join(" "),
        );
        const name = normalizeSearchText(user.name);
        const cpf = normalizeSearchText(userCpf);
        const startsWithQuery =
          Boolean(normalizedQuery) &&
          (name.startsWith(normalizedQuery) || cpf.startsWith(normalizedQuery));

        return {
          startsWithQuery,
          user,
          visible: !normalizedQuery || searchable.includes(normalizedQuery),
        };
      })
      .filter((item) => item.visible)
      .sort((first, second) => {
        if (first.startsWithQuery !== second.startsWithQuery) {
          return first.startsWithQuery ? -1 : 1;
        }

        return first.user.name.localeCompare(second.user.name);
      });

    return matches.slice(0, MAX_PLAYER_RESULTS).map((item) => item.user);
  }, [availableUsers, excludedCpf, normalizedQuery]);

  const shouldShowResults = isOpen && !disabled;

  function selectPlayer(user: AuthUser) {
    const selectedValue = getSelectedValue(user);
    onValueChange(selectedValue);
    setIsOpen(false);
    onSelect?.(user, selectedValue);
  }

  return (
    <div
      className={`admin-player-picker${
        shouldShowResults ? " admin-player-picker--open" : ""
      }`}
      onBlur={(event) => {
        const nextTarget = event.relatedTarget;
        if (!nextTarget || !event.currentTarget.contains(nextTarget as Node)) {
          setIsOpen(false);
        }
      }}
    >
      <input
        id={id}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-expanded={shouldShowResults}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          onValueChange(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
      />
      {shouldShowResults && (
        <div
          id={listboxId}
          className="admin-player-picker__results"
          role="listbox"
        >
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <button
                key={`admin-user-option-${id}-${user.id}`}
                type="button"
                className="admin-player-picker__option"
                role="option"
                aria-selected={formatAdminPlayerOption(user) === value}
                onPointerDown={(event) => {
                  event.preventDefault();
                  selectPlayer(user);
                }}
                onClick={() => selectPlayer(user)}
              >
                <span>{user.name}</span>
                <small>{normalizeCpf(user.cpf)}</small>
              </button>
            ))
          ) : (
            <span className="admin-player-picker__empty">
              Nenhum jogador encontrado.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
